import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const HOUSEHOLDS = [
  { value: "1", label: "1 · Solo tower" },
  { value: "2", label: "2 · Partnered" },
  { value: "3-4", label: "3–4 · Small crew" },
  { value: "5+", label: "5+ · Full manifest" },
];

const PERSONAS = [
  { value: "dual_income", label: "Dual-Income Family" },
  { value: "chronic_illness", label: "Chronic Illness Household" },
  { value: "single_parent", label: "Single Parent" },
  { value: "sandwich_generation", label: "Sandwich Generation" },
  { value: "military_veteran", label: "Military / Veteran Family" },
  { value: "underserved", label: "Underserved Family" },
  { value: "other", label: "Something else entirely" },
];

export default function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [household, setHousehold] = useState("");
  const [persona, setPersona] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/waitlist/count")
      .then((r) => {
        if (!cancelled) setCount(r.data.count);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !household || !persona) {
      toast.error("Fill all three fields to proceed.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/waitlist", {
        email: email.trim().toLowerCase(),
        household_size: household,
        persona,
      });
      setDone(true);
      toast.success("Clearance granted. Welcome to the waitlist.");
      // refresh count
      api.get("/waitlist/count").then((r) => setCount(r.data.count));
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative border-t border-rule py-24 md:py-32"
    >
      <div className="pointer-events-none absolute inset-0 hud-grid-fine opacity-20" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-obsidian to-transparent" />

      <div className="relative mx-auto max-w-4xl px-6 md:px-10">
        <div className="text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
            06 — Boarding
          </p>
          <h2 className="mt-6 font-display text-4xl leading-[1.05] text-ink md:text-5xl">
            Request clearance for
            <br />
            <span className="italic text-mist">the private preview.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-mist">
            Early flights carry a small crew. Tell us who you are and we&apos;ll
            reach out when your tower is ready.
          </p>
        </div>

        <div className="relative mx-auto mt-14 max-w-2xl">
          <span className="absolute -top-2 -left-2 h-4 w-4 border-t border-l border-teal/70" />
          <span className="absolute -top-2 -right-2 h-4 w-4 border-t border-r border-teal/70" />
          <span className="absolute -bottom-2 -left-2 h-4 w-4 border-b border-l border-teal/70" />
          <span className="absolute -bottom-2 -right-2 h-4 w-4 border-b border-r border-teal/70" />

          <div className="border border-rule bg-deep/70 p-8 backdrop-blur-sm md:p-10">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col items-center text-center"
                data-testid="waitlist-success"
              >
                <CheckCircle2
                  className="h-10 w-10 text-signalGreen"
                  strokeWidth={1.5}
                />
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.26em] text-signalGreen">
                  Clearance granted
                </p>
                <h3 className="mt-4 font-display text-2xl text-ink md:text-3xl">
                  You&apos;re on the manifest.
                </h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-mist">
                  We&apos;ll radio when your tower is ready. In the meantime, feel
                  free to walk the preview.
                </p>
                <a
                  href="/login"
                  data-testid="waitlist-preview-link"
                  className="mt-8 inline-flex items-center gap-2 border border-rule px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:border-amber"
                >
                  Sign in
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                </a>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <label
                    htmlFor="wl-email"
                    className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist"
                  >
                    Email · call sign
                  </label>
                  <input
                    id="wl-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@household.tower"
                    data-testid="waitlist-email-input"
                    className="mt-2 w-full border border-rule bg-obsidian px-4 py-3.5 text-ink placeholder:text-mist/50 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
                  />
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="wl-household"
                      className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist"
                    >
                      Household size
                    </label>
                    <select
                      id="wl-household"
                      required
                      value={household}
                      onChange={(e) => setHousehold(e.target.value)}
                      data-testid="waitlist-household-select"
                      className="mt-2 w-full appearance-none border border-rule bg-obsidian px-4 py-3.5 text-ink focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
                      style={{
                        backgroundImage:
                          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1L6 6L11 1' stroke='%239BA6B5' stroke-width='1.5'/></svg>\")",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 1rem center",
                      }}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {HOUSEHOLDS.map((h) => (
                        <option key={h.value} value={h.value}>
                          {h.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="wl-persona"
                      className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist"
                    >
                      Family profile
                    </label>
                    <select
                      id="wl-persona"
                      required
                      value={persona}
                      onChange={(e) => setPersona(e.target.value)}
                      data-testid="waitlist-persona-select"
                      className="mt-2 w-full appearance-none border border-rule bg-obsidian px-4 py-3.5 text-ink focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal"
                      style={{
                        backgroundImage:
                          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1L6 6L11 1' stroke='%239BA6B5' stroke-width='1.5'/></svg>\")",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 1rem center",
                      }}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {PERSONAS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="waitlist-submit-btn"
                  className="group flex w-full items-center justify-center gap-2 bg-teal px-6 py-4 font-mono text-[12px] uppercase tracking-[0.24em] text-ink transition-colors duration-300 hover:bg-teal-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Transmitting…" : "Request clearance"}
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
                    strokeWidth={1.5}
                  />
                </button>

                <p className="text-center font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                  {count !== null ? (
                    <span data-testid="waitlist-count">
                      {count.toLocaleString()} households already on the manifest
                    </span>
                  ) : (
                    <span>Private preview · quiet by design</span>
                  )}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
